import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import {
  GrcNotify,
  NotifyType,
  PagedQuery,
  PagedResult,
  ServerPagingOptions
} from '@grc/ui-package';
import useGetApiData from '../services/get-api-data';
import usePostApiData from '../services/post-api-data';
import usePutApiData from '../services/put-api-data';
import { platformAdminService } from '../services/axios.constant';
import {
  CreateTenantRequest,
  TenantDetails,
  UpdateTenantRequest
} from './tenant-types';
import { fetchTenantPage } from './tenant-paging';

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string; message?: string } | undefined;
    return data?.detail || data?.title || data?.message || err.message || fallback;
  }
  return fallback;
}

const tenantsUrl = '/tenant';

export default function useTenants() {
  const { fetchData } = useGetApiData();
  const { postData } = usePostApiData();
  const { putData } = usePutApiData();
  // Bumped after a write so the grid reloads from page 1.
  const [refreshKey, setRefreshKey] = useState(0);
  // Two flows, two states. The old single slot encoded "closed" as undefined and
  // "create" as null, so every consumer had to know which of the two it was looking at.
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState<TenantDetails | null>(null);

  const loadTenants = useCallback(async () => setRefreshKey(key => key + 1), []);

  // The grid pages, filters and sorts on GET /v2/tenant (AB#7601).
  const fetchPage = useCallback(
    (query: PagedQuery): Promise<PagedResult<TenantDetails>> =>
      fetchTenantPage(
        (url, params) => fetchData<PagedResult<TenantDetails>>(url, platformAdminService, params),
        query
      ),
    [fetchData]
  );

  const onError = useCallback(
    (err: unknown) =>
      GrcNotify({
        status: NotifyType.Error,
        message: extractErrorMessage(err, 'Failed to load tenants.')
      }),
    []
  );

  const serverPaging: ServerPagingOptions<TenantDetails> = useMemo(
    () => ({ fetchPage, onError, refreshKey }),
    [fetchPage, onError, refreshKey]
  );

  const createTenant = useCallback(
    async (request: CreateTenantRequest) => {
      try {
        await postData(tenantsUrl, platformAdminService, request);
      } catch (err) {
        GrcNotify({
          status: NotifyType.Error,
          message: extractErrorMessage(err, 'Failed to create tenant.')
        });
        throw err;
      }
      await loadTenants();
      setCreateOpen(false);
    },
    [postData, loadTenants]
  );

  const updateTenant = useCallback(
    async (id: string, request: UpdateTenantRequest) => {
      try {
        await putData(`${tenantsUrl}/${id}`, platformAdminService, request);
      } catch (err) {
        GrcNotify({
          status: NotifyType.Error,
          message: extractErrorMessage(err, 'Failed to update tenant.')
        });
        throw err;
      }
      await loadTenants();
      setEditTenant(null);
    },
    [putData, loadTenants]
  );

  const activateTenant = useCallback(
    async (id: string) => {
      try {
        await putData(`${tenantsUrl}/${id}/activate`, platformAdminService);
      } catch (err) {
        GrcNotify({
          status: NotifyType.Error,
          message: extractErrorMessage(err, 'Failed to activate tenant.')
        });
        throw err;
      }
      await loadTenants();
      setEditTenant(null);
    },
    [putData, loadTenants]
  );

  const openCreate = useCallback(() => setCreateOpen(true), []);
  const closeCreate = useCallback(() => setCreateOpen(false), []);
  const openEdit = useCallback((tenant: TenantDetails) => setEditTenant(tenant), []);
  const closeEdit = useCallback(() => setEditTenant(null), []);

  return {
    serverPaging,
    createOpen,
    editTenant,
    openCreate,
    closeCreate,
    openEdit,
    closeEdit,
    createTenant,
    updateTenant,
    activateTenant
  };
}
